/**
 * Big Data Backend Service
 * Gère le traitement de fichiers volumineux côté serveur
 */

import { debugLog } from '../utils/debug-logger';
import { authFetch } from '@/utils/api-headers';
import { API_URL } from './config'; // ✅ Import depuis config centralisé
import logger from '../utils/logger';

export interface BackendJob {
  id: string;
  status: 'starting' | 'downloading' | 'parsing' | 'analyzing' | 'indexing' | 'completed' | 'error';
  progress: number;
  fileName: string;
  fileSize: number;
  startedAt: string;
  completedAt?: string;
  metadata?: {
    rowCount: number;
    columnCount: number;
    columns: Array<{ name: string; type: string }>;
    tableName: string;
    datasetId: string;
  };
  error?: string;
}

export interface QueryResult {
  success: boolean;
  data: any[];
  rowCount: number;
  executionTime: number;
}

class BigDataBackendService {
  /**
   * Upload fichier volumineux vers le backend
   */
  async uploadLargeFile(
    file: File,
    userId: string,
    onProgress?: (progress: number) => void
  ): Promise<{ jobId: string; s3Key: string }> {
    const formData = new FormData();
    formData.append('file', file);
    formData.append('userId', userId);

    logger.log('[Backend] 🌐 Endpoint:', `${API_URL}/api/big-data-server/upload`);
    logger.log('[Backend] 📦 Fichier:', file.name, `(${(file.size / 1024 / 1024).toFixed(2)}MB)`);
    logger.log('[Backend] 👤 User ID:', userId);

    return new Promise((resolve, reject) => {
      const xhr = new XMLHttpRequest();
      const uploadTimeout = setTimeout(() => {
        xhr.abort();
        logger.error('[Backend] ⏱️ TIMEOUT after 10 minutes');
        reject(new Error('Upload timeout - fichier trop volumineux ou connexion lente'));
      }, 10 * 60 * 1000); // 10 minutes timeout

      // Progress tracking
      if (onProgress) {
        xhr.upload.addEventListener('progress', (e) => {
          if (e.lengthComputable) {
            const percentComplete = (e.loaded / e.total) * 100;
            onProgress(percentComplete);
          }
        });
      }

      xhr.addEventListener('load', () => {
        clearTimeout(uploadTimeout);
        
        logger.log('[Backend] 📬 Response received - Status:', xhr.status);
        
        if (xhr.status >= 200 && xhr.status < 300) {
          try {
            const response = JSON.parse(xhr.responseText);
            logger.log('[Backend] ✅ Upload réussi - Job ID:', response.jobId);
            logger.log('[Backend] 📍 S3 Key:', response.s3Key);
            resolve(response);
          } catch (e) {
            logger.error('[Backend] ❌ Invalid JSON response:', xhr.responseText);
            reject(new Error('Invalid response format'));
          }
        } else {
          logger.error('[Backend] ❌ HTTP Error:', xhr.status, xhr.statusText);
          logger.error('[Backend] Response body:', xhr.responseText);
          reject(new Error(`Upload failed: ${xhr.status} ${xhr.statusText}`));
        }
      });

      xhr.addEventListener('error', (e) => {
        clearTimeout(uploadTimeout);
        logger.error('[Backend] ❌ NETWORK ERROR - Backend non accessible');
        logger.error('[Backend] ❌ Target URL:', `${API_URL}/api/big-data-server/upload`);
        logger.error('[Backend] ❌ CAUSE POSSIBLE:');
        logger.error('  1. Backend not running → Run: node expert-server.js');
        logger.error('  2. CORS issue → Check expert-server.js CORS config');
        logger.error('  3. Firewall blocking request');
        logger.error('  4. Wrong API_URL in .env');
        reject(new Error(`Erreur réseau - Le backend n'est pas accessible à ${API_URL}`));
      });

      xhr.addEventListener('abort', () => {
        clearTimeout(uploadTimeout);
        logger.error('[Backend] ❌ Upload annulé');
        reject(new Error('Upload cancelled'));
      });

      try {
        xhr.open('POST', `${API_URL}/api/big-data-server/upload`, true);
        logger.log('[Backend] 📤 Sending XHR request...');
        xhr.send(formData);
      } catch (e) {
        clearTimeout(uploadTimeout);
        logger.error('[Backend] ❌ Failed to open XHR:', e);
        reject(e);
      }
    });
  }

  /**
   * Poll job status
   */
  async getJobStatus(jobId: string): Promise<BackendJob> {
    const response = await authFetch(`${API_URL}/api/big-data-server/job/${jobId}`);
    
    if (!response.ok) {
      throw new Error(`Failed to get job status: ${response.statusText}`);
    }

    return response.json();
  }

  /**
   * Poll job status jusqu'à completion
   */
  async waitForCompletion(
    jobId: string,
    onProgress?: (job: BackendJob) => void,
    pollInterval = 2000
  ): Promise<BackendJob> {
    logger.log('[Backend] 🔄 Démarrage polling job status...', jobId);
    
    return new Promise((resolve, reject) => {
      let pollCount = 0;
      
      const poll = async () => {
        try {
          pollCount++;
          logger.log(`[Backend] 🔄 Poll #${pollCount} - Fetching job status...`);
          
          const job = await this.getJobStatus(jobId);
          
          logger.log(`[Backend] 📊 Job status: ${job.status} - ${job.progress}%`);
          
          if (onProgress) {
            onProgress(job);
          }

          if (job.status === 'completed') {
            logger.log('[Backend] ✅ Job completed!', {
              rowCount: job.metadata?.rowCount,
              columnCount: job.metadata?.columnCount
            });
            resolve(job);
          } else if (job.status === 'error') {
            logger.error('[Backend] ❌ Job failed:', job.error);
            reject(new Error(job.error || 'Processing failed'));
          } else {
            logger.log(`[Backend] ⏳ Still processing... Next poll in ${pollInterval}ms`);
            setTimeout(poll, pollInterval);
          }
        } catch (error) {
          logger.error('[Backend] ❌ Polling error:', error);
          reject(error);
        }
      };

      poll();
    });
  }

  /**
   * Execute SQL query sur un dataset traité
   */
  async executeQuery(jobId: string, query: string): Promise<QueryResult> {
    const response = await authFetch(`${API_URL}/api/big-data-server/query`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
      },
      body: JSON.stringify({ jobId, query }),
    });

    if (!response.ok) {
      const error = await response.json();
      throw new Error(error.message || 'Query failed');
    }

    return response.json();
  }

  /**
   * Get sample data
   */
  async getSample(jobId: string, limit = 100): Promise<QueryResult> {
    const response = await authFetch(`${API_URL}/api/big-data-server/sample`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
      },
      body: JSON.stringify({ jobId, limit }),
    });

    if (!response.ok) {
      throw new Error('Failed to get sample');
    }

    return response.json();
  }

  /**
   * Get all jobs for user
   */
  async getUserJobs(userId: string): Promise<{ jobs: BackendJob[]; count: number }> {
    const response = await authFetch(
      `${API_URL}/api/big-data-server/jobs?userId=${userId}`
    );

    if (!response.ok) {
      throw new Error('Failed to get jobs');
    }

    return response.json();
  }

  /**
   * Delete job
   */
  async deleteJob(jobId: string): Promise<void> {
    const response = await authFetch(`${API_URL}/api/big-data-server/job/${jobId}`, {
      method: 'DELETE',
    });

    if (!response.ok) {
      throw new Error('Failed to delete job');
    }
  }

  /**
   * Process large file - all in one
   */
  async processLargeFile(
    file: File,
    userId: string,
    onUploadProgress?: (progress: number) => void,
    onProcessingProgress?: (job: BackendJob) => void
  ): Promise<BackendJob> {
    logger.log('[Backend] 🚀 Starting processLargeFile...');
    
    // Step 1: Upload
    logger.log('[Backend] 📤 Step 1/2: Uploading to S3...');
    const { jobId, s3Key } = await this.uploadLargeFile(file, userId, onUploadProgress);
    logger.log('[Backend] ✅ Upload complete!', { jobId, s3Key });
    
    // Step 2: Wait for processing
    logger.log('[Backend] ⚙️ Step 2/2: Waiting for backend processing...');
    const result = await this.waitForCompletion(jobId, onProcessingProgress);
    
    logger.log('[Backend] 🎉 processLargeFile COMPLETED!');
    return result;
  }
}

// Singleton instance
let backendServiceInstance: BigDataBackendService | null = null;

export const getBigDataBackendService = (): BigDataBackendService => {
  if (!backendServiceInstance) {
    backendServiceInstance = new BigDataBackendService();
  }
  return backendServiceInstance;
};

export default BigDataBackendService;

